import { Directive, effect, inject, input, output, Signal, signal, viewChild, WritableSignal } from '@angular/core';

import { CheckboxState } from '@connect-services/client/lib/components/checkbox/checkbox.component';
import { PaginationSdkParams } from '@connect-services/client/services/fetch-all.service';
import { GlobalSearchService } from '@connect-services/client/services/global-search.service';
import { TableService } from '@connect-services/client/services/table.service';
import { PaginatorComponent } from '@connect-services/src/lib/components/paginator/paginator.component';
import { defaultPaginationParameters, OrderEnum, PaginationParameters } from '@shared/client/models/pagination.model';

type BlocLike<T> = {
    load(...args: unknown[]): Promise<void>;
    value(): T | null;
};

export interface PaginatedLike {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    page: number;
    take: number;
    totalItems: number;
    totalPages: number;
}

@Directive()
export abstract class AbstractNotificationSelectListComponent<
    TBloc extends BlocLike<TPaginated>,
    TPaginated extends PaginatedLike,
> {
    public readonly allSelected = input.required<boolean>();
    public readonly paginatedData: WritableSignal<TPaginated | null> = signal(null);
    public paginationParameters: PaginationParameters = {
        ...defaultPaginationParameters,
        order: OrderEnum.ASC,
        take: 9,
    };
    public readonly selectedItems = input.required<string[]>();
    public toggleSelect = output<{ all: boolean; id?: string }>();

    protected readonly CheckboxState = CheckboxState;
    protected readonly globalSearchService = inject(GlobalSearchService);
    protected readonly paginator: Signal<PaginatorComponent | undefined> = viewChild<PaginatorComponent>('paginator');
    protected readonly tableService = inject(TableService);

    protected abstract readonly bloc: TBloc;

    public constructor() {
        effect(() => {
            if (!this.bloc.value()) {
                return;
            }

            this.paginatedData.set(this.bloc.value());
        });
    }

    public async onPaginationChange(page: number): Promise<void> {
        const pageInformation: PaginationSdkParams = {
            page: page,
            take: this.paginatedData()?.take,
        };

        this.paginationParameters = this.tableService.onPaginationChange(pageInformation, this.paginationParameters);
        await this.loadBloc(this.globalSearchService.searchValue()!);
    }

    public async search(searchValue: string): Promise<void> {
        this.globalSearchService.searchValue.set(searchValue);
        this.paginator()?.resetPaginator();
        await this.loadBloc(this.globalSearchService.searchValue()!);
    }

    protected async loadBloc(searchValue?: string): Promise<void> {
        await this.bloc.load(
            {
                ...this.paginationParameters,
                ...(searchValue && { properties: this.globalSearchService.columnsToSearchValueIn }),
                value: searchValue,
            },
            { refresh: true },
        );
    }
}
